// src/main.tsx
// ============================================================================
// FILE ROLE: Application bootstrap - configures Amplify, FCM, and service worker
//
// TRUST BOUNDARY: public/unauth bootstrap → delegates to authed flows via App
//
// SECURITY HARDENING:
//   - Input validation on all FCM payloads (whitelist-based)
//   - Rate limiting on event dispatches (prevents spam)
//   - Conditional logging (dev only, reduces console noise in prod)
//   - Type safety for all external data
//   - Error monitoring integration (Sentry)
//   - Service worker update notifications
//   - Performance monitoring (dev only)
//
// DEPENDENCIES:
//   - aws-amplify (auth/API config)
//   - firebase (cloud messaging)
//   - react, react-dom (UI framework)
//   - @sentry/react (error monitoring - optional, graceful if missing)
// ============================================================================

import React from "react";
import ReactDOM from "react-dom/client";
import App from "./App";
import "./index.css";

import { Amplify } from "aws-amplify";
import { Authenticator } from "@aws-amplify/ui-react";
import outputs from "../amplify_outputs.json";

import { onMessage, MessagePayload } from "firebase/messaging";
import { messaging } from "./firebase";

import posthog from "posthog-js";

// ============================================================================
// ENVIRONMENT & CONFIGURATION (Must be at top for Sentry init)
// ============================================================================

const IS_DEV = import.meta.env.DEV;
const IS_PROD = import.meta.env.PROD;
const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN;
const POSTHOG_KEY = import.meta.env.VITE_POSTHOG_KEY as string | undefined;

// Sentry - import only if installed (graceful fallback)
let Sentry: any = null;
(async () => {
  try {
    const sentryModule = await import("@sentry/react");
    Sentry = sentryModule;
    
    // Initialize Sentry immediately after import (production only)
    if (IS_PROD && SENTRY_DSN) {
      Sentry.init({
        dsn: SENTRY_DSN,
        environment: 'production',
        enabled: true,
        
        // Performance monitoring
        tracesSampleRate: 0.1, // 10% of transactions (saves quota)
        
        // Session replay (optional - uses more quota)
        replaysSessionSampleRate: 0.1, // 10% of sessions
        replaysOnErrorSampleRate: 1.0, // 100% of sessions with errors
        
        // Ignore common non-critical errors
        ignoreErrors: [
          'ResizeObserver loop limit exceeded',
          'Non-Error promise rejection',
          'ResizeObserver loop completed with undelivered notifications',
        ],
        
        // Filter out noise
        beforeSend(event: any) {
          // Don't send events from browser extensions
          if (event.exception?.values?.[0]?.stacktrace?.frames?.some(
            (frame: any) => frame.filename?.includes('chrome-extension://')
          )) {
            return null;
          }
          return event;
        },
      });
      
      if (IS_DEV) void ["✅ Sentry initialized"];
    } else if (IS_PROD && !SENTRY_DSN) {
      if (IS_DEV) void ["⚠️ Sentry DSN not configured - errors won't be monitored"];
    }
  } catch {
    // Sentry not installed - continue without it
  }
})();

// ============================================================================
// POSTHOG ANALYTICS
// ============================================================================

if (POSTHOG_KEY) {
  posthog.init(POSTHOG_KEY, {
    api_host: "https://us.i.posthog.com",
    person_profiles: "identified_only",
  });
}

// Valid notification types (whitelist - only these are processed)
const VALID_NOTIFICATION_TYPES = new Set([
  'MATCH_REVEAL',
  'MATCH_PENDING',
  'NEW_MATCH',
  'SECRET_ADMIRER',
  'SYSTEM_MESSAGE'
] as const);

type ValidNotificationType = 'MATCH_REVEAL' | 'MATCH_PENDING' | 'NEW_MATCH' | 'SECRET_ADMIRER' | 'SYSTEM_MESSAGE';

// Legacy backend values still in use in some Lambda paths.
// Map them so foreground notifications still render and refresh logic still triggers.
const LEGACY_NOTIFICATION_TYPE_MAP: Record<string, ValidNotificationType> = {
  MATCH: 'NEW_MATCH',
  PENDING_VERIFICATION: 'MATCH_PENDING',
  POTENTIAL_MATCH: 'MATCH_PENDING',
};

// Match-related notifications that trigger UI refresh
const MATCH_REFRESH_TYPES = new Set<ValidNotificationType>([
  'MATCH_REVEAL',
  'MATCH_PENDING',
  'NEW_MATCH'
]);

// Rate limiting for refresh events (prevents spam/abuse)
const REFRESH_EVENT_COOLDOWN_MS = 1000; // 1 second minimum between refreshes
let lastRefreshEventTime = 0;

// Service worker update check interval (production only)
const SW_UPDATE_CHECK_INTERVAL_MS = 60 * 60 * 1000; // 1 hour

// ============================================================================
// LOGGING UTILITIES (Development-only by default)
// ============================================================================

const log = {
  info: (...args: unknown[]) => {
    if (IS_DEV) void [...args];
  },
  warn: (...args: unknown[]) => {
    if (IS_DEV) void [...args];
  },
  error: (...args: unknown[]) => {
    // Always log errors, even in production
    void [...args];
    // Send to Sentry if available
    if (Sentry && args[0] instanceof Error) {
      Sentry.captureException(args[0]);
    }
  },
  success: (...args: unknown[]) => {
    if (IS_DEV) void [...args];
  }
};

// ============================================================================
// ERROR BOUNDARY UI
// ============================================================================

interface ErrorScreenProps {
  error?: Error;
  resetError?: () => void;
}

function ErrorScreen({ error, resetError }: ErrorScreenProps) {
  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100vh',
      fontFamily: 'system-ui, -apple-system, sans-serif',
      padding: '2rem',
      backgroundColor: '#f9fafb'
    }}>
      <div style={{
        textAlign: 'center',
        maxWidth: '500px',
        backgroundColor: 'white',
        padding: '3rem',
        borderRadius: '1rem',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
        border: '1px solid #e5e7eb'
      }}>
        <div style={{
          width: '64px',
          height: '64px',
          margin: '0 auto 1.5rem',
          borderRadius: '50%',
          backgroundColor: '#fee2e2',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '32px'
        }}>
          ⚠️
        </div>
        
        <h1 style={{
          color: '#e54c4c',
          marginBottom: '1rem',
          fontSize: '1.5rem',
          fontWeight: 'bold'
        }}>
          Service Unavailable
        </h1>
        
        <p style={{
          color: '#666',
          marginBottom: '1.5rem',
          lineHeight: '1.6'
        }}>
          We're having trouble connecting to our services. Please try refreshing the page.
        </p>
        
        {IS_DEV && error && (
          <details style={{
            marginBottom: '1.5rem',
            padding: '1rem',
            backgroundColor: '#fef2f2',
            borderRadius: '0.5rem',
            textAlign: 'left'
          }}>
            <summary style={{ cursor: 'pointer', fontWeight: '600', color: '#991b1b' }}>
              Error Details (Development Only)
            </summary>
            <pre style={{
              marginTop: '0.5rem',
              fontSize: '0.75rem',
              color: '#7f1d1d',
              overflow: 'auto',
              maxHeight: '200px',
              whiteSpace: 'pre-wrap'
            }}>
              {error.message}
              {'\n\n'}
              {error.stack}
            </pre>
          </details>
        )}
        
        <button
          onClick={resetError || (() => window.location.reload())}
          style={{
            background: '#e54c4c',
            color: 'white',
            border: 'none',
            padding: '0.75rem 2rem',
            borderRadius: '0.5rem',
            cursor: 'pointer',
            fontSize: '1rem',
            fontWeight: '600',
            transition: 'background 0.2s'
          }}
          onMouseEnter={(e) => (e.currentTarget.style.background = '#d43c3c')}
          onMouseLeave={(e) => (e.currentTarget.style.background = '#e54c4c')}
        >
          Refresh Page
        </button>
      </div>
    </div>
  );
}

// ============================================================================
// AMPLIFY CONFIGURATION
// ============================================================================

try {
  Amplify.configure(outputs);
  log.success("✅ Amplify configured successfully");
} catch (err) {
  const error = err instanceof Error ? err : new Error(String(err));
  log.error("❌ Amplify configuration failed:", error);
  
  const errorRoot = document.getElementById("root");
  if (errorRoot) {
    const root = ReactDOM.createRoot(errorRoot);
    root.render(<ErrorScreen error={error} />);
  }
  
  throw error; // Re-throw for monitoring
}

// ============================================================================
// FIREBASE CLOUD MESSAGING - INPUT VALIDATION
// ============================================================================

/**
 * Validates notification type against whitelist
 */
function isValidNotificationType(type: unknown): type is ValidNotificationType {
  return typeof type === 'string' && VALID_NOTIFICATION_TYPES.has(type as any);
}

/**
 * Validates and sanitizes FCM payload
 */
function validateFCMPayload(payload: MessagePayload): {
  isValid: boolean;
  notificationType?: ValidNotificationType;
  title: string;
  body: string;
} {
  const notification = payload.notification ?? {};
  const title = typeof notification.title === 'string' ? notification.title : 'Crushhh';
  const body = typeof notification.body === 'string' ? notification.body : 'You have a new notification';
  
  const rawType = payload.data?.type;
  let notificationType: ValidNotificationType | undefined;
  
  if (isValidNotificationType(rawType)) {
    notificationType = rawType;
  } else if (typeof rawType === 'string' && LEGACY_NOTIFICATION_TYPE_MAP[rawType]) {
    notificationType = LEGACY_NOTIFICATION_TYPE_MAP[rawType];
  } else if (rawType != null) {
    log.warn("[FCM] Unknown notification type:", rawType);
  }
  
  return {
    isValid: true,
    notificationType,
    title,
    body
  };
}

// ============================================================================
// FIREBASE CLOUD MESSAGING - FOREGROUND HANDLER
// ============================================================================

if (messaging) {
  onMessage(messaging, async (payload: MessagePayload) => {
    log.info("[FCM] Foreground message received:", payload);
    
    // Validate payload
    const validated = validateFCMPayload(payload);
    
    if (!validated.isValid) {
      log.warn("[FCM] Skipping invalid notification payload");
      return;
    }
    
    const { notificationType, title, body } = validated;
    if (notificationType) {
      log.info("[FCM] Notification type:", notificationType);
    } else {
      log.info("[FCM] No recognized type; showing notification without refresh event");
    }
    
    // Dispatch match refresh event (with rate limiting)
    if (notificationType && MATCH_REFRESH_TYPES.has(notificationType)) {
      const now = Date.now();
      
      if (now - lastRefreshEventTime > REFRESH_EVENT_COOLDOWN_MS) {
        log.info("[FCM] 🔄 Dispatching match refresh event");
        
        try {
          window.dispatchEvent(new CustomEvent('crushhh-refresh-matches', {
            detail: {
              type: notificationType,
              timestamp: now,
              // Don't include full payload - potential PII
            }
          }));
          
          lastRefreshEventTime = now;
        } catch (err) {
          log.error("[FCM] Failed to dispatch refresh event:", err);
        }
      } else {
        log.warn("[FCM] ⏱️ Refresh event throttled (too soon since last)");
      }
    }
    
    // Display notification
    try {
      const registration = await navigator.serviceWorker.getRegistration();
      
      if (registration && 'showNotification' in registration) {
        await registration.showNotification(title, {
          body,
          icon: '/favicon-96x96.png',
          badge: '/favicon-96x96.png',
          data: notificationType ? { type: notificationType } : {}, // Minimal data only
          tag: 'crushhh-notification',
          requireInteraction: false,
        });
        log.success("[FCM] ✅ Notification displayed");
      } else if ('Notification' in window && Notification.permission === 'granted') {
        // Fallback to Notification API
        new Notification(title, {
          body,
          icon: '/favicon-96x96.png',
          badge: '/favicon-96x96.png',
          tag: 'crushhh-notification',
        });
        log.success("[FCM] ✅ Notification displayed (fallback)");
      } else {
        log.warn("[FCM] ⚠️ Notifications not supported or permission not granted");
      }
    } catch (err) {
      log.error("[FCM] ❌ Notification display failed:", err);
    }
  });
} else {
  log.warn("⚠️ Firebase Cloud Messaging not supported (likely HTTP or old browser)");
}

// ============================================================================
// SERVICE WORKER REGISTRATION & UPDATE STRATEGY
// ============================================================================

if ('serviceWorker' in navigator) {
  window.addEventListener('load', async () => {
    try {
      const registration = await navigator.serviceWorker.register(
        '/firebase-messaging-sw.js',
        { scope: '/' }
      );
      
      log.success("✅ Service Worker registered, scope:", registration.scope);
      
      // ✅ NEW: Force immediate update check
      registration.update().catch((err) => {
        log.error("Service Worker immediate update failed:", err);
      });
      
      // Handle waiting service worker (update available)
      if (registration.waiting) {
        try {
          registration.waiting.postMessage({ type: 'SKIP_WAITING' });
          log.info("📦 Service Worker update applied (SKIP_WAITING)");
        } catch (err) {
          log.warn("⚠️ SKIP_WAITING message failed:", err);
        }
      }
      
      // Listen for service worker updates
      registration.addEventListener('updatefound', () => {
        const newWorker = registration.installing;
        if (!newWorker) return;
        
        newWorker.addEventListener('statechange', () => {
          if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
            log.info("📦 New service worker available");
            
            // Dispatch event that UI can listen to for "Update Available" banner
            window.dispatchEvent(new CustomEvent('crushhh-update-available'));
          }
        });
      });
      
      // Periodically check for updates (production only)
      if (IS_PROD) {
        setInterval(() => {
          registration.update().catch((err) => {
            log.error("Service Worker update check failed:", err);
          });
        }, SW_UPDATE_CHECK_INTERVAL_MS);
        
        log.info("📦 Service Worker auto-update enabled (checking every hour)");
      }
      
    } catch (err) {
      log.error("❌ Service Worker registration failed:", err);
      // Service worker failure is not critical - app can still function
    }
  });
} else {
  log.warn("⚠️ Service Worker not supported in this browser");
  log.warn("Push notifications and offline support will not be available");
}

// ============================================================================
// REACT APP MOUNT
// ============================================================================

const rootElement = document.getElementById('root');

if (!rootElement) {
  const error = new Error('Missing <div id="root"></div> in index.html');
  log.error(error);
  throw error;
}

const root = ReactDOM.createRoot(rootElement);

// Render app (Sentry errors auto-captured via global handlers)
root.render(
  <React.StrictMode>
    <Authenticator.Provider>
      <App />
    </Authenticator.Provider>
  </React.StrictMode>
);

// ============================================================================
// GLOBAL ERROR HANDLERS (Production Monitoring)
// ============================================================================

if (IS_PROD) {
  // Catch unhandled errors
  window.addEventListener('error', (event) => {
    log.error('Unhandled error:', event.error);
    // Sentry auto-captures if initialized
  });
  
  // Catch unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    log.error('Unhandled promise rejection:', event.reason);
    // Sentry auto-captures if initialized
  });
}

// ============================================================================
// PERFORMANCE MONITORING (Development Only)
// ============================================================================

if (IS_DEV && 'performance' in window && 'getEntriesByType' in performance) {
  window.addEventListener('load', () => {
    setTimeout(() => {
      const perfData = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined;
      
      if (perfData) {
        log.info('📊 Performance metrics:', {
          'DNS Lookup': `${Math.round(perfData.domainLookupEnd - perfData.domainLookupStart)}ms`,
          'TCP Connection': `${Math.round(perfData.connectEnd - perfData.connectStart)}ms`,
          'Request/Response': `${Math.round(perfData.responseEnd - perfData.requestStart)}ms`,
          'DOM Processing': `${Math.round(perfData.domContentLoadedEventEnd - perfData.domContentLoadedEventStart)}ms`,
          'Total Load Time': `${Math.round(perfData.loadEventEnd - perfData.fetchStart)}ms`,
        });
      }
    }, 0);
  });
}

log.success("🚀 Application initialized successfully");
